import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getStaffDetailPath, getStaffReturnPath, getStaffSection, withStaffReturnPath } from '../../app/router/staffNavigation';
import { useEffect, useMemo, useRef, useState } from "react";
import { HOME_ROUTE } from "../../app/router/routes";
import { LayoutPages } from "../layoutPages";
import { useAppSelector } from "../../app/store/store";
import { IIdmCreateStaffMember, IIdmFeature, IIdmRole, idmService } from "../../entities/idm";
import { useGlobalMessageActions } from "../../entities/globalMessage";
import { useGlobalLoadingActions } from "../../entities/globalLoading";
import { FEATURES, FEATURE_SECTIONS, hasFeature, useMyActions } from "../../entities/my";
import { MyError } from "../../shared/lib/error/MyError";
import { AuthError } from "../../shared/lib/error/AuthError";
import { MyInput } from "../../shared/ui/input";
import { MyButton } from "../../shared/ui/button";
import { MyCheckbox } from "../../shared/ui/myCheckbox/myCheckbox";
import classes from "./idmCreate.module.scss";
import { IStudentTag, studentService } from "../../entities/student";
import { ChooseItems } from "../../features/chooseItems";
import { TagSelector } from "../../features/tagSelector";
import { IItem } from "../../shared/model/types";
import { WorkTgSelector } from "../../features/workTgSelector";
import { workTgService } from "../../entities/workTg";

interface IFormError {
    field: string;
    text: string;
}

const initialStaff: IIdmCreateStaffMember = {
    email: '',
    full_name: '',
    phone: '',
    tg: '',
    legacy_role_id: 0,
    idm_role_ids: [],
    idm_feature_ids: [],
    relations: [],
    tutor_meta: {
        cost_per_hour: '',
        cost_per_shift: '',
        desired_hours: '',
        tag_ids: [],
        gender: '',
    },
    work_tg_ids: [],
    cost_per_month: '',
}

export default function IdmCreatePage() {
    const router = useNavigate()
    const {search} = useLocation()
    const requestedReturn = new URLSearchParams(search).get('returnTo') || '/staff'
    const section = getStaffSection(requestedReturn.split('?')[0])
    const backPath = getStaffReturnPath(search, section)
    const {my} = useAppSelector(s => s.myReducer)
    const canCreateStaff = hasFeature(my, FEATURES.IDM.STAFF_CREATE)
    const canListRoles = hasFeature(my, FEATURES.IDM.ROLES_LIST)
    const canAssignStaffTags = hasFeature(my, FEATURES.IDM.STAFF_TAGS_ASSIGN)
    const canListWorkTgs = hasFeature(my, FEATURES.WORK_TGS.LIST)
    const isAccess = hasFeature(my, FEATURE_SECTIONS.STAFF) && canCreateStaff && canListRoles

    const [staff, setStaff] = useState<IIdmCreateStaffMember>(initialStaff)
    const [roles, setRoles] = useState<IIdmRole[]>([])
    const [tags, setTags] = useState<IStudentTag[]>([])
    const [workTgItems, setWorkTgItems] = useState<IItem[]>([])
    const [formError, setFormError] = useState<IFormError[]>([])
    const [selectedRoleFeatures, setSelectedRoleFeatures] = useState<IIdmFeature[]>([])
    const [isRoleFeaturesLoading, setIsRoleFeaturesLoading] = useState(false)
    const [roleFeaturesError, setRoleFeaturesError] = useState('')
    const roleFeaturesRequestId = useRef(0)

    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsLoading} = useGlobalLoadingActions()
    const {setIsAuth} = useMyActions()

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: defaultMessage, type: 'error'})
        }
    }

    const getError = (field: string) => formError.find(item => item.field === field)?.text

    const clearError = (field: string) => {
        setFormError(prev => prev.filter(item => item.field !== field))
    }

    const setField = <K extends keyof IIdmCreateStaffMember>(field: K) => {
        return (value: IIdmCreateStaffMember[K]) => {
            setStaff(prev => ({...prev, [field]: value}))
            clearError(String(field))
        }
    }

    const loadData = async () => {
        try{
            setIsLoading(true)
            const [rolesRes, tagsRes, workTgsRes] = await Promise.all([
                idmService.listRoles(),
                studentService.getTags(),
                canListWorkTgs ? workTgService.getAll() : Promise.resolve([])
            ])
            setRoles(rolesRes.sort((a, b) => a.hierarchy_level - b.hierarchy_level || a.name.localeCompare(b.name)))
            if(section === 'performers'){
                const role = rolesRes.find(item => item.code === 'tutor')
                if(role) setStaff(current => ({...current, idm_role_ids: [role.id]}))
            }
            setTags(tagsRes)
            setWorkTgItems(workTgsRes.map(workTg => ({id: workTg.id, name: workTg.name})))
        }
        catch(e){
            handleError(e, 'Ошибка при подготовке формы создания сотрудника')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getRoleTitle = (role: IIdmRole) => role.name.trim().split(/\s+/)[0] || role.name

    const tutorRole = useMemo(
        () => roles.find(role => {
            const normalizedRoleCode = role.code.trim().toLowerCase()
            const normalizedRoleName = role.name.trim().toLowerCase()
            return normalizedRoleCode.includes('tutor')
                || normalizedRoleName.includes('репетитор')
                || normalizedRoleName.includes('tutor')
        }) || null,
        [roles]
    )

    const coordinatorRole = useMemo(
        () => roles.find(role => {
            const normalizedRoleCode = role.code.trim().toLowerCase()
            const normalizedRoleName = role.name.trim().toLowerCase()
            return normalizedRoleCode.includes('coordinator')
                || normalizedRoleName.includes('координатор')
                || normalizedRoleName.includes('coordinator')
        }) || null,
        [roles]
    )

    const curatorRole = useMemo(
        () => roles.find(role => {
            const normalizedRoleCode = role.code.trim().toLowerCase()
            const normalizedRoleName = role.name.trim().toLowerCase()
            return normalizedRoleCode.includes('curator')
                || normalizedRoleName.includes('куратор')
                || normalizedRoleName.includes('curator')
        }) || null,
        [roles]
    )

    const hrRole = useMemo(
        () => roles.find(role => {
            const normalizedRoleCode = role.code.trim().toLowerCase()
            const normalizedRoleName = role.name.trim().toLowerCase()
            return normalizedRoleCode === 'hr'
                || normalizedRoleName === 'hr'
                || normalizedRoleName.includes('hr')
        }) || null,
        [roles]
    )

    const isTutorRoleSelected = tutorRole ? staff.idm_role_ids.includes(tutorRole.id) : false
    const isCoordinatorRoleSelected = coordinatorRole ? staff.idm_role_ids.includes(coordinatorRole.id) : false
    const isCuratorRoleSelected = curatorRole ? staff.idm_role_ids.includes(curatorRole.id) : false
    const isHrRoleSelected = hrRole ? staff.idm_role_ids.includes(hrRole.id) : false
    const shouldRequestTutorRate = isTutorRoleSelected || isCoordinatorRoleSelected || isCuratorRoleSelected
    const shouldRequestRate = shouldRequestTutorRate || isHrRoleSelected
    const shouldRequestCostPerShift = isCoordinatorRoleSelected || isCuratorRoleSelected
    const rateField = isHrRoleSelected
        ? 'cost_per_month'
        : shouldRequestCostPerShift
            ? 'tutor_meta.cost_per_shift'
            : 'tutor_meta.cost_per_hour'
    const rateTitle = isHrRoleSelected
        ? 'Зарплата в месяц'
        : shouldRequestCostPerShift
            ? 'Стоимость смены'
            : 'Стоимость часа'
    const ratePlaceholder = isHrRoleSelected
        ? 'Например: 70000'
        : shouldRequestCostPerShift
            ? 'Например: 3500'
            : 'Например: 1500'
    const selectedRole = roles.find(role => staff.idm_role_ids.includes(role.id)) || null

    const loadRoleFeaturesForCreation = async (roleId: number) => {
        const requestId = ++roleFeaturesRequestId.current
        setSelectedRoleFeatures([])
        setRoleFeaturesError('')
        setIsRoleFeaturesLoading(true)

        try{
            const roleFeaturesRes = await idmService.listRoleFeaturesForCreation(roleId)
            if(requestId !== roleFeaturesRequestId.current) return

            const featureIds = roleFeaturesRes.features.map(feature => feature.id)
            setSelectedRoleFeatures(roleFeaturesRes.features)
            setStaff(prev => prev.idm_role_ids.includes(roleId)
                ? {...prev, idm_feature_ids: featureIds}
                : prev
            )
        }
        catch(e){
            if(requestId !== roleFeaturesRequestId.current) return

            setRoleFeaturesError(e instanceof MyError
                ? e.message
                : 'Не удалось загрузить базовые фичи выбранной роли'
            )
        }
        finally{
            if(requestId === roleFeaturesRequestId.current){
                setIsRoleFeaturesLoading(false)
            }
        }
    }

    const selectRole = (roleId: number) => {
        setStaff(prev => ({
            ...prev,
            idm_role_ids: [roleId],
            idm_feature_ids: [],
        }))
        clearError('idm_role_ids')
        clearError('idm_feature_ids')
        loadRoleFeaturesForCreation(roleId)
    }

    const toggleFeatureSelection = (featureId: number) => {
        return (selected: boolean) => {
            setStaff(prev => ({
                ...prev,
                idm_feature_ids: selected
                    ? [...prev.idm_feature_ids.filter(id => id !== featureId), featureId]
                    : prev.idm_feature_ids.filter(id => id !== featureId),
            }))
            clearError('idm_feature_ids')
        }
    }

    const toggleAllFeatures = (featureIds: number[]) => {
        return (selected: boolean) => {
            setStaff(prev => ({
                ...prev,
                idm_feature_ids: selected ? featureIds : [],
            }))
            clearError('idm_feature_ids')
        }
    }

    const validate = () => {
        const nextErrors: IFormError[] = []
        if(!staff.email.trim()) nextErrors.push({field: 'email', text: 'Обязательное поле'})
        if(!staff.full_name.trim()) nextErrors.push({field: 'full_name', text: 'Обязательное поле'})
        if(!staff.phone.trim()) nextErrors.push({field: 'phone', text: 'Обязательное поле'})
        if(!staff.tg.trim()) nextErrors.push({field: 'tg', text: 'Обязательное поле'})
        if(staff.idm_role_ids.length === 0){
            nextErrors.push({field: 'idm_role_ids', text: 'Выберите роль'})
        }
        if(isRoleFeaturesLoading){
            nextErrors.push({field: 'idm_feature_ids', text: 'Дождитесь загрузки фич выбранной роли'})
        }
        else if(roleFeaturesError){
            nextErrors.push({field: 'idm_feature_ids', text: 'Не удалось загрузить фичи выбранной роли'})
        }
        else if(selectedRoleFeatures.length > 0 && staff.idm_feature_ids.length === 0){
            nextErrors.push({field: 'idm_feature_ids', text: 'Выберите хотя бы одно право'})
        }
        if(canListWorkTgs && staff.work_tg_ids.length === 0){
            nextErrors.push({field: 'work_tg_ids', text: 'Выберите хотя бы один рабочий ТГ'})
        }
        if(shouldRequestRate){
            const rateValue = isHrRoleSelected
                ? staff.cost_per_month.trim()
                : shouldRequestCostPerShift
                    ? staff.tutor_meta?.cost_per_shift?.trim()
                    : staff.tutor_meta?.cost_per_hour?.trim()

            if(!rateValue){
                nextErrors.push({
                    field: rateField,
                    text: isHrRoleSelected
                        ? 'Укажите зарплату в месяц'
                        : shouldRequestCostPerShift
                            ? 'Укажите стоимость смены'
                            : 'Укажите стоимость часа'
                })
            }
        }
        if(isTutorRoleSelected){
            const desiredHours = staff.tutor_meta?.desired_hours.trim() || ''
            if(!/^\d+$/.test(desiredHours) || Number(desiredHours) <= 0){
                nextErrors.push({field: 'tutor_meta.desired_hours', text: 'Укажите целое число часов в неделю'})
            }
        }

        setFormError(nextErrors)
        return nextErrors.length === 0
    }

    const onCreate = async () => {
        if(!validate()){
            return
        }
        try{
            setIsLoading(true)
            const createdUserId = await idmService.createStaffMember({
                ...staff,
                email: staff.email.trim(),
                full_name: staff.full_name.trim(),
                phone: staff.phone.trim(),
                tg: staff.tg.trim(),
                cost_per_month: isHrRoleSelected ? staff.cost_per_month.trim() : '',
                tutor_meta: shouldRequestTutorRate ? {
                    cost_per_hour: isTutorRoleSelected ? (staff.tutor_meta?.cost_per_hour?.trim() || '') : '',
                    cost_per_shift: shouldRequestCostPerShift ? (staff.tutor_meta?.cost_per_shift?.trim() || '') : '',
                    desired_hours: isTutorRoleSelected ? (staff.tutor_meta?.desired_hours.trim() || '') : '',
                    tag_ids: isTutorRoleSelected && canAssignStaffTags ? (staff.tutor_meta?.tag_ids || []) : [],
                    gender: isTutorRoleSelected ? staff.tutor_meta?.gender ?? '' : '',
                } : undefined,
                relations: []
            })
            setGlobalMessage({message: 'Сотрудник успешно создан', type: 'ok'})
            router(withStaffReturnPath(getStaffDetailPath(createdUserId, isTutorRoleSelected ? 'performers' : 'workers'), backPath))
        }
        catch(e){
            handleError(e, 'Ошибка при создании сотрудника')
        }
        finally{
            setIsLoading(false)
        }
    }

    if(!isAccess){
        return <Navigate to={HOME_ROUTE.path} replace />
    }

    return (
        <LayoutPages title={section === 'performers' ? 'Создать исполнителя' : 'Создать работника'} titleLink={backPath}>
            <section className={classes.container}>
                <h1 className={classes.title}>Создание сотрудника</h1>

                <MyInput
                    title="Email"
                    value={staff.email}
                    setValue={setField('email')}
                    error={getError('email')}
                    setError={() => clearError('email')}
                />
                <MyInput
                    title="ФИО сотрудника"
                    value={staff.full_name}
                    setValue={setField('full_name')}
                    error={getError('full_name')}
                    setError={() => clearError('full_name')}
                />
                <MyInput
                    title="Телефон"
                    value={staff.phone}
                    setValue={setField('phone')}
                    error={getError('phone')}
                    setError={() => clearError('phone')}
                />
                <MyInput
                    title="Telegram"
                    value={staff.tg}
                    setValue={setField('tg')}
                    error={getError('tg')}
                    setError={() => clearError('tg')}
                />
                {
                    canListWorkTgs &&
                    <section className={classes.block}>
                        <section className={classes.blockTitle}>Доступные рабочие ТГ</section>
                        <WorkTgSelector
                            title="Рабочие ТГ"
                            selectedItems={workTgItems.filter(item => staff.work_tg_ids.includes(Number(item.id)))}
                            onChange={(items) => {
                                setStaff(prev => ({
                                    ...prev,
                                    work_tg_ids: items.map(item => Number(item.id)).filter(id => id > 0),
                                }))
                                clearError('work_tg_ids')
                            }}
                            getData={async () => workTgItems}
                            multiple
                            placeholder="Выберите рабочие ТГ"
                            reloadTrigger={workTgItems.length}
                            error={getError('work_tg_ids')}
                            setError={() => clearError('work_tg_ids')}
                        />
                    </section>
                }
                {
                    shouldRequestRate &&
                    <section className={classes.block}>
                        <section className={classes.blockTitle}>
                            {
                                isTutorRoleSelected
                                    ? 'Данные исполнителя'
                                    : isCoordinatorRoleSelected
                                        ? 'Данные координатора'
                                        : isCuratorRoleSelected
                                            ? 'Данные куратора'
                                            : 'Данные HR'
                            }
                        </section>
                        <section className={classes.tutorMetaGrid}>
                            <MyInput
                                title={rateTitle}
                                value={isHrRoleSelected
                                    ? staff.cost_per_month
                                    : shouldRequestCostPerShift
                                        ? (staff.tutor_meta?.cost_per_shift || '')
                                        : (staff.tutor_meta?.cost_per_hour || '')}
                                setValue={(value) => {
                                    setStaff(prev => ({
                                        ...prev,
                                        cost_per_month: isHrRoleSelected ? value : prev.cost_per_month,
                                        tutor_meta: {
                                            cost_per_hour: shouldRequestCostPerShift ? (prev.tutor_meta?.cost_per_hour || '') : value,
                                            cost_per_shift: shouldRequestCostPerShift ? value : (prev.tutor_meta?.cost_per_shift || ''),
                                            desired_hours: prev.tutor_meta?.desired_hours || '',
                                            tag_ids: prev.tutor_meta?.tag_ids ?? [],
                                            gender: prev.tutor_meta?.gender ?? '',
                                        }
                                    }))
                                    clearError(rateField)
                                }}
                                error={getError(rateField)}
                                setError={() => clearError(rateField)}
                                placeholder={ratePlaceholder}
                            />
                            {isTutorRoleSelected && (
                                <MyInput
                                    title="Желаемые часы в неделю"
                                    type="number"
                                    min="1"
                                    step="1"
                                    inputMode="numeric"
                                    value={staff.tutor_meta?.desired_hours || ''}
                                    setValue={(value) => {
                                        setStaff(prev => ({
                                            ...prev,
                                            tutor_meta: {
                                                cost_per_hour: prev.tutor_meta?.cost_per_hour || '',
                                                cost_per_shift: prev.tutor_meta?.cost_per_shift || '',
                                                desired_hours: value,
                                                tag_ids: prev.tutor_meta?.tag_ids ?? [],
                                                gender: prev.tutor_meta?.gender ?? '',
                                            }
                                        }))
                                        clearError('tutor_meta.desired_hours')
                                    }}
                                    error={getError('tutor_meta.desired_hours')}
                                    setError={() => clearError('tutor_meta.desired_hours')}
                                    placeholder="Например: 20"
                                />
                            )}
                            {
                                isTutorRoleSelected && canAssignStaffTags &&
                                <TagSelector
                                    title="Теги"
                                    selectedItems={tags
                                        .filter(tag => staff.tutor_meta?.tag_ids?.includes(tag.id))
                                        .map<IItem>(tag => ({id: tag.id, name: tag.tag_name}))
                                    }
                                    onChange={(items) => {
                                        setStaff(prev => ({
                                            ...prev,
                                            tutor_meta: {
                                                cost_per_hour: prev.tutor_meta?.cost_per_hour || '',
                                                cost_per_shift: prev.tutor_meta?.cost_per_shift || '',
                                                desired_hours: prev.tutor_meta?.desired_hours || '',
                                                tag_ids: items.map(item => Number(item.id)).filter(id => id > 0),
                                                gender: prev.tutor_meta?.gender ?? '',
                                            }
                                        }))
                                        clearError('tutor_meta.tag_ids')
                                    }}
                                    getData={async () => tags.map(tag => ({id: tag.id, name: tag.tag_name}))}
                                    error={getError('tutor_meta.tag_ids')}
                                    setError={() => clearError('tutor_meta.tag_ids')}
                                    canCreate
                                    createPlaceholder="Например: ОГЭ"
                                    onCreate={async (tagName) => {
                                        const createdTag = await studentService.createTag(tagName)
                                        setTags(prev => [...prev.filter(tag => tag.id !== createdTag.id), createdTag]
                                            .sort((first, second) => first.tag_name.localeCompare(second.tag_name)))
                                        return {id: createdTag.id, name: createdTag.tag_name}
                                    }}
                                />
                            }
                            {
                                isTutorRoleSelected &&
                                <ChooseItems
                                    title="Пол исполнителя"
                                    selectedItems={staff.tutor_meta?.gender === 'male' ? [1] : staff.tutor_meta?.gender === 'female' ? [2] : []}
                                    setItem={(id) => {
                                        setStaff(prev => ({
                                            ...prev,
                                            tutor_meta: {
                                                cost_per_hour: prev.tutor_meta?.cost_per_hour || '',
                                                cost_per_shift: prev.tutor_meta?.cost_per_shift || '',
                                                desired_hours: prev.tutor_meta?.desired_hours || '',
                                                tag_ids: prev.tutor_meta?.tag_ids ?? [],
                                                gender: id === 1 ? 'male' : id === 2 ? 'female' : '',
                                            }
                                        }))
                                    }}
                                    getData={async () => [
                                        {id: 1, name: 'Мужской'},
                                        {id: 2, name: 'Женский'},
                                    ]}
                                />
                            }
                        </section>
                    </section>
                }
                <section className={classes.block}>
                    <section className={classes.blockHeader}>
                        <section className={classes.blockTitle}>IDM-роль и права при создании</section>
                    </section>
                    <section className={classes.roleTabs} role="tablist" aria-label="Роли сотрудника">
                        {roles.map(role => {
                            const isSelected = selectedRole?.id === role.id
                            return <button
                                key={role.id}
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                className={classes.roleTab + (isSelected ? ` ${classes.roleTabActive}` : '')}
                                onClick={() => selectRole(role.id)}
                            >
                                {getRoleTitle(role)}
                            </button>
                        })}
                    </section>
                    {selectedRole ? (
                        <section className={classes.roleContent}>
                            <section className={classes.roleDescription}>
                                Базовые фичи для роли «{getRoleTitle(selectedRole)}».
                            </section>
                            <section className={classes.checkboxList}>
                                {isRoleFeaturesLoading && (
                                    <section className={classes.relationEmpty}>Загружаем базовые фичи роли…</section>
                                )}
                                {!isRoleFeaturesLoading && roleFeaturesError && (
                                    <section className={classes.selectError}>{roleFeaturesError}</section>
                                )}
                                {!isRoleFeaturesLoading && !roleFeaturesError && selectedRoleFeatures.length > 0 && (
                                    <section className={classes.checkboxItem}>
                                        <MyCheckbox
                                            checked={selectedRoleFeatures.every(feature => staff.idm_feature_ids.includes(feature.id))}
                                            onSelected={toggleAllFeatures(selectedRoleFeatures.map(feature => feature.id))}
                                            label="Выбрать все"
                                        />
                                    </section>
                                )}
                                {!isRoleFeaturesLoading && !roleFeaturesError && selectedRoleFeatures.map(feature => (
                                    <section key={feature.id} className={classes.checkboxItem}>
                                        <MyCheckbox
                                            checked={staff.idm_feature_ids.includes(feature.id)}
                                            onSelected={toggleFeatureSelection(feature.id)}
                                            label={`${feature.name} (${feature.key})`}
                                        />
                                        {feature.description && (
                                            <section className={classes.featureDescription}>
                                                {feature.description}
                                            </section>
                                        )}
                                    </section>
                                ))}
                                {!isRoleFeaturesLoading && !roleFeaturesError && selectedRoleFeatures.length === 0 && (
                                    <section className={classes.relationEmpty}>Для выбранной роли нет базовых фич.</section>
                                )}
                            </section>
                        </section>
                    ) : (
                        <section className={classes.roleDescription}>Выберите роль, чтобы увидеть доступные фичи.</section>
                    )}
                    {getError('idm_role_ids') && (
                        <section className={classes.selectError}>{getError('idm_role_ids')}</section>
                    )}
                    {getError('idm_feature_ids') && (
                        <section className={classes.selectError}>{getError('idm_feature_ids')}</section>
                    )}
                </section>

                <section className={classes.actions}>
                    <MyButton onClick={onCreate} error={formError.length ? 'Заполните обязательные поля' : ''}>
                        Создать сотрудника
                    </MyButton>
                </section>
            </section>
        </LayoutPages>
    )
}
